import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { FileText, LogOut, Watch, X } from 'lucide-react';
import '../css/GuardianControlCenter.css';
import tokens from '../design/tokens.json';
import RiskBadge from '../components/RiskBadge';
import { Chart as ChartJS } from 'chart.js/auto';
import { API_BASE_URL } from '../config/config';
import 'chartjs-adapter-date-fns';
import { ko } from 'date-fns/locale';
import HealthReport from './HealthReport';

// 차트 색은 토큰에서만 온다(design.md 1절). 눈금 글자는 흰 바탕 대비를 위해 text-2 를 쓴다
// (chart.axis 는 3.04:1 이라 글자에 모자란다). RecordsTab.js 와 같은 규칙이다.
const chartColor = tokens.color.chart;
const statusColor = tokens.color.status;
const textColor = tokens.color.text;

// 피보호자 카드에 보이는 지표. 순서가 화면 순서다.
const METRICS = [
    { type: 'BLOOD_GLUCOSE', label: '혈당(식전)', unit: 'mg/dL' },
    { type: 'BLOOD_GLUCOSE_MEAL', label: '혈당(식후)', unit: 'mg/dL' },
    { type: 'BP_SYSTOLIC', label: '혈압(수축기)', unit: 'mmHg' },
    { type: 'BP_DIASTOLIC', label: '혈압(이완기)', unit: 'mmHg' },
    { type: 'BMI', label: 'BMI', unit: '' }
];

const MODAL_TITLES = {
    BLOOD_GLUCOSE: '식전/공복혈당 상세보기',
    BLOOD_GLUCOSE_MEAL: '식후혈당 상세보기',
    BP_SYSTOLIC: '혈압 상세보기',
    BP_DIASTOLIC: '혈압 상세보기',
    BMI: '체질량지수 상세보기'
};

// 위험도 계산 함수
const getRiskLevel = (value, type) => {
    switch (type) {
        case 'BLOOD_GLUCOSE':
            if (value < 70) return { level: "위험 (저혈당)", color: "#000080" };
            if (value <= 100) return { level: "정상", color: "#2ECC40" };
            if (value <= 125) return { level: "경고 (고혈당 주의)", color: "#FFDC00" };
            if (value > 125) return { level: "위험 (고혈당)", color: "#FF4136" };
            return { level: "기록 없음", color: "#AAAAAA" };
        case 'BLOOD_GLUCOSE_MEAL':
            if (value < 90) return { level: "위험 (저혈당)", color: "#000080" };
            if (value <= 140) return { level: "정상", color: "#2ECC40" };
            if (value <= 200) return { level: "경고 (고혈당 주의)", color: "#FFDC00" };
            if (value > 200) return { level: "위험 (고혈당)", color: "#FF4136" };
            return { level: "기록 없음", color: "#AAAAAA" };
        case 'BP_SYSTOLIC':
            if (value < 90) return { level: "위험 (저혈압)", color: "#000080" };
            if (value <= 120) return { level: "정상", color: "#2ECC40" };
            if (value <= 140) return { level: "경고 (고혈압 주의)", color: "#FFDC00" };
            if (value > 140) return { level: "위험 (고혈압)", color: "#FF4136" };
            return { level: "기록 없음", color: "#AAAAAA" };
        case 'BP_DIASTOLIC':
            if (value < 60) return { level: "위험 (저혈압)", color: "#000080" };
            if (value <= 80) return { level: "정상", color: "#2ECC40" };
            if (value <= 90) return { level: "경고 (고혈압 주의)", color: "#FFDC00" };
            if (value > 90) return { level: "위험 (고혈압)", color: "#FF4136" };
            return { level: "기록 없음", color: "#AAAAAA" };
        case 'BMI':
            if (value < 18.5) return { level: "위험 (저체중)", color: "#000080" };
            if (value <= 23.0) return { level: "정상", color: "#2ECC40" };
            if (value <= 25.0) return { level: "경고 (과체중 주의)", color: "#FFDC00" };
            if (value > 25.0) return { level: "위험 (비만)", color: "#FF4136" };
            return { level: "기록 없음", color: "#AAAAAA" };
        default:
            return { level: "기록 없음", color: "#AAAAAA" };
    }
};

// 안전 범위 표시 플러그인
const drawSafeRanges = (chart) => {
    const { ctx, chartArea, scales } = chart;
    if (!ctx || !chartArea || !scales) return;
    const safeRanges = chart.options.safeRanges;
    if (!Array.isArray(safeRanges)) return;
    safeRanges.forEach(range => {
        if (!range || range.min == null || range.max == null) return;
        ctx.save();
        ctx.fillStyle = range.color;
        ctx.fillRect(
            chartArea.left,
            scales.y.getPixelForValue(range.max),
            chartArea.right - chartArea.left,
            scales.y.getPixelForValue(range.min) - scales.y.getPixelForValue(range.max)
        );
        ctx.beginPath();
        ctx.strokeStyle = range.lineColor;
        ctx.lineWidth = 2;
        ctx.moveTo(chartArea.left, scales.y.getPixelForValue(range.max));
        ctx.lineTo(chartArea.right, scales.y.getPixelForValue(range.max));
        ctx.moveTo(chartArea.left, scales.y.getPixelForValue(range.min));
        ctx.lineTo(chartArea.right, scales.y.getPixelForValue(range.min));
        ctx.stroke();
        ctx.restore();
    });
};

// 플러그인 등록
ChartJS.register({
    id: 'safeRangesPlugin',
    beforeDraw: (chart) => {
        if (chart.options && typeof chart.options.getSafeRanges === 'function') {
            chart.options.safeRanges = chart.options.getSafeRanges();
        }
        drawSafeRanges(chart);
    }
});

export default function GuardianControlCenter() {
    const [data, setData] = useState({});
    const [activeTab, setActiveTab] = useState('BLOOD_GLUCOSE');
    const [selectedUser, setSelectedUser] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [safeRange, setSafeRange] = useState(null);
    const navigate = useNavigate();
    const [filterType, setFilterType] = useState('all');
    const [systolicRange, setSystolicRange] = useState(null);
    const [diastolicRange, setDiastolicRange] = useState(null);
    const [mappedUsers, setMappedUsers] = useState([]);
    const [healthReportModalUser, setHealthReportModalUser] = useState(null);
    // 스마트링 착용자인 피보호자의 app_user_id 목록. 서버가 guardian_dependent_config 를
    // 다시 읽어 판정하므로 토큰의 dependents 와 별개로 조회한다.
    const [smartRingAppUserIds, setSmartRingAppUserIds] = useState(() => new Set());

    // 컴포넌트 마운트 시 보호자 정보 로드
    useEffect(() => {
        // console.log('보호자 페이지 마운트');

        // 토큰에서 보호자 정보 로드
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decodedToken = jwtDecode(token);
                if (decodedToken.dependents) {
                    // console.log('토큰에서 피보호자 정보 로드:', decodedToken.dependents);
                    setMappedUsers(decodedToken.dependents);
                }
            } catch (error) {
                console.error('토큰 디코딩 실패:', error);
            }
        }

        fetchHealthData();
        fetchSmartRingWearers();
    }, []);

    // 스마트링 착용자 목록 로드 (실패해도 버튼만 안 보일 뿐 화면은 그대로)
    const fetchSmartRingWearers = async () => {
        const token = localStorage.getItem('token');
        if (!token) return;
        try {
            const response = await axios.get(`${API_BASE_URL}/api/guardian-smart-ring-wearers`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const ids = response.data?.data?.appUserIds;
            if (Array.isArray(ids)) {
                setSmartRingAppUserIds(new Set(ids.map(Number)));
            }
        } catch (error) {
            console.error('스마트링 착용자 조회 실패:', error.message);
        }
    };

    // 건강 데이터 로드 함수
    const fetchHealthData = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                // console.log('토큰이 없어서 건강 데이터를 로드할 수 없습니다.');
                return;
            }

            const response = await axios.get(`${API_BASE_URL}/api/guardian-health-data`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // console.log('API 응답 전체:', response);
            // console.log('API 응답 데이터:', response.data);

            if (response.data.status === 'success') {
                // console.log('건강 데이터 로드 성공:', response.data.data);
                const healthData = response.data.data || {};

                // 데이터가 객체 형태인 경우 그대로 사용 (management_id별로 그룹화된 데이터)
                if (healthData && typeof healthData === 'object' && !Array.isArray(healthData)) {
                    // console.log('건강 데이터 로드 성공:', healthData);
                    // console.log('건강 데이터의 키들:', Object.keys(healthData));
                    // console.log('건강 데이터 샘플:', Object.entries(healthData).slice(0, 2));
                    setData(healthData);
                } else {
                    console.error('건강 데이터 형식이 올바르지 않습니다:', healthData);
                }
            } else {
                console.error('건강 데이터 로드 실패:', response.data?.message || '알 수 없는 오류');
            }
        } catch (error) {
            console.error('건강 데이터 로드 중 오류:', error);
        }
    };

    // 토큰 검증 로직 추가 (보호자 모드에서는 토큰이 없어도 접근 허용)
    useEffect(() => {
        const token = localStorage.getItem('token');
        const userRole = localStorage.getItem('user_role');

        // 보호자 모드이거나 임시 토큰인 경우 토큰 검증 스킵
        if (userRole === 'guardian' || (token && token.startsWith('guardian_temp_token_'))) {
            // console.log('보호자 모드로 접근합니다.');
            return;
        }

        // 토큰이 없는 경우
        if (!token) {
            alert('로그인이 필요합니다.');
            navigate('/');
            return;
        }

        // 일반 토큰 검증
        try {
            const decodedToken = jwtDecode(token);
            const currentTime = Date.now() / 1000;

            if (decodedToken.exp < currentTime) {
                alert('세션이 만료되었습니다. 다시 로그인해주세요.');
                localStorage.removeItem('token');
                navigate('/');
                return;
            }
        } catch (error) {
            console.error('토큰 검증 실패:', error);
            // 보호자 페이지이므로 토큰 검증 실패 시에도 진행 허용
            // console.log('토큰 검증에 실패했지만 보호자 페이지로 진행합니다.');
        }
    }, [navigate]);

    // 필터 변경 이벤트 감지
    useEffect(() => {
        const handleFilterChange = (event) => {
            setFilterType(event.detail.type);
        };
        window.addEventListener('filterChange', handleFilterChange);
        return () => window.removeEventListener('filterChange', handleFilterChange);
    }, []);

    // 안전범위 동적 조회 함수
    const fetchSafeRange = async (measureType) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/api/measurement-signal-config`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { measure_type: measureType }
            });
            if (response.data && response.data.success && response.data.data) {
                if (measureType === 'BP_SYSTOLIC' || measureType === 'BP_DIASTOLIC') {
                    // NORMAL, WARNING 구간 모두 추출
                    const ranges = response.data.data
                        .filter(row => row.signal_type === 'NORMAL' || row.signal_type === 'WARNING')
                        .map(row => {
                            let min = Number(row.greater_than_equal);
                            let max = row.lower_than_equal !== null && row.lower_than_equal !== undefined
                                ? Number(row.lower_than_equal)
                                : Number(row.lower_than);
                            // 하한값 보정
                            if (measureType === 'BP_SYSTOLIC' && min < 90) min = 90;
                            if (measureType === 'BP_DIASTOLIC' && min < 60) min = 60;
                            return {
                                min,
                                max,
                                text: `${row.signal_type === 'NORMAL' ? '정상' : '경고'} ${measureType === 'BP_SYSTOLIC' ? '수축기' : '이완기'} 혈압 범위 (${min}~${max})`,
                                // 색은 토큰에서만 온다(design.md 1절). 구간의 뜻(정상/경고)만 색으로 보인다.
                                color: row.signal_type === 'NORMAL' ? chartColor['band-normal'] : chartColor['band-caution'],
                                lineColor: row.signal_type === 'NORMAL' ? statusColor.normal.line : statusColor.caution.line,
                                textColor: row.signal_type === 'NORMAL' ? statusColor.normal.fg : statusColor.caution.fg
                            };
                        });
                    return ranges;
                } else {
                    // 기존 로직 유지 (NORMAL만)
                    const normalConfig = response.data.data.find(row => row.signal_type === 'NORMAL');
                    if (normalConfig) {
                        const min = Number(normalConfig.greater_than_equal);
                        const max = normalConfig.lower_than_equal !== null && normalConfig.lower_than_equal !== undefined
                            ? Number(normalConfig.lower_than_equal)
                            : Number(normalConfig.lower_than);
                        return {
                            min,
                            max,
                            text: measureType === 'BLOOD_GLUCOSE' ? `정상 혈당 범위 (${min}~${max})` : `정상 BMI 범위 (${min}~${max})`
                        };
                    }
                }
            }
            return measureType === 'BP_SYSTOLIC' || measureType === 'BP_DIASTOLIC' ? [] : null;
        } catch (e) {
            return measureType === 'BP_SYSTOLIC' || measureType === 'BP_DIASTOLIC' ? [] : null;
        }
    };

    const fetchAllSafeRanges = async () => {
        const [sysRanges, diaRanges] = await Promise.all([
            fetchSafeRange('BP_SYSTOLIC'),
            fetchSafeRange('BP_DIASTOLIC')
        ]);
        setSystolicRange(sysRanges);
        setDiastolicRange(diaRanges);
        return { sysRanges, diaRanges };
    };

    // 날짜 포맷터
    const dateFormatter = (dateString) => {
        const date = new Date(dateString);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    const handleUserClick = async (user, measureType) => {
        // console.log('Grid Item Clicked - User:', user);
        // console.log('Measure Type:', measureType);
        // console.log('Current data structure:', data);

        // measureType을 activeTab으로 설정
        setActiveTab(measureType);

        // user 객체에서 managementId를 가져와서 데이터 찾기
        const managementId = user.managementId;
        // console.log('User managementId:', managementId);

        let rawUserData = data[String(managementId)] || [];
        // console.log('Raw User Data with managementId:', rawUserData);

        // managementId로 찾지 못한 경우, 다른 방법으로 찾기
        if (rawUserData.length === 0) {
            // console.log('ManagementId로 데이터를 찾지 못함. data의 모든 키:', Object.keys(data));

            // user.id로도 시도
            rawUserData = data[String(user.id)] || [];
            // console.log('Raw User Data with user.id:', rawUserData);
        }

        // console.log('Final rawUserData:', rawUserData);

        let userData = rawUserData.map(record => ({
            ...record,
            measure_date: new Date(record.measure_date).toISOString()
        }));

        // measureType에 따른 데이터 필터링
        if (measureType === 'BP_SYSTOLIC' || measureType === 'BP_DIASTOLIC') {
            const bpData = userData.filter(record =>
                record.measure_type === 'BP_SYSTOLIC' || record.measure_type === 'BP_DIASTOLIC'
            );
            if (bpData.length > 0) {
                userData = bpData;
                // console.log('Blood Pressure Data filtered:', bpData);
            }
        } else if (measureType === 'BLOOD_GLUCOSE') {
            const bgData = userData.filter(record =>
                record.measure_type === 'BLOOD_GLUCOSE'
            );
            if (bgData.length > 0) {
                userData = bgData;
                // console.log('Blood Glucose Data filtered:', bgData);
            }
        } else if (measureType === 'BLOOD_GLUCOSE_MEAL') {
            const bgmData = userData.filter(record =>
                record.measure_type === 'BLOOD_GLUCOSE_MEAL'
            );
            if (bgmData.length > 0) {
                userData = bgmData;
                // console.log('Blood Glucose Meal Data filtered:', bgmData);
            }
        } else if (measureType === 'BMI') {
            const bmiData = userData.filter(record =>
                record.measure_type === 'BMI'
            );
            if (bmiData.length > 0) {
                userData = bmiData;
                // console.log('BMI Data filtered:', bmiData);
            }
        }

        // console.log('Filtered userData for measureType:', measureType, userData);

        if (userData.length === 0) {
            userData = [{
                name: '-',
                birthdate: '-',
                gender: '-',
                management_group: '-',
                current_value: '-',
                measure_date: new Date().toISOString(),
                measure_type: measureType
            }];
            // console.log('No Data Available - Using Default Data');
        }

        userData = userData.sort((a, b) => new Date(a.measure_date) - new Date(b.measure_date));

        let safeRangeValue = null, diastolicRangeValue = null;
        // ⚠️ activeTab 이 아니라 measureType 을 쓴다. setActiveTab 은 비동기라 여기서 activeTab 은 아직
        //    이전 탭 값이다 — 예전에는 혈압 152/94 를 혈당 기준으로 판정해 '정상'이라고 보였다(2026-09-15).
        if (measureType === 'BP_SYSTOLIC' || measureType === 'BP_DIASTOLIC') {
            const { sysRanges, diaRanges } = await fetchAllSafeRanges();
            safeRangeValue = sysRanges;
            diastolicRangeValue = diaRanges;
        } else if (measureType === 'BLOOD_GLUCOSE') {
            safeRangeValue = await fetchSafeRange('BLOOD_GLUCOSE');
            setSystolicRange(safeRangeValue);
            setDiastolicRange(null);
        } else if (measureType === 'BMI') {
            safeRangeValue = await fetchSafeRange('BMI');
            setSystolicRange(safeRangeValue);
            setDiastolicRange(null);
        }
        // console.log('Safe Range Settings:', safeRangeValue, diastolicRangeValue);

        const latestRecord = userData[userData.length - 1]; // 가장 최근 기록 사용

        // 혈압은 수축기·이완기 두 기록이다. 가장 최근 수축기·이완기 값을 "수축기/이완기" 로 보이고
        // 위험도는 수축기로 판정한다(기록 표와 같은 기준).
        const isBp = measureType === 'BP_SYSTOLIC' || measureType === 'BP_DIASTOLIC';
        const latestOf = (type) => [...userData].reverse().find(r => r.measure_type === type);
        const latestSystolic = isBp ? latestOf('BP_SYSTOLIC') : null;
        const latestDiastolic = isBp ? latestOf('BP_DIASTOLIC') : null;
        const riskBase = isBp ? latestSystolic : latestRecord;
        const latestValue = isBp
            ? (latestSystolic || latestDiastolic ? `${latestSystolic?.current_value ?? '-'}/${latestDiastolic?.current_value ?? '-'}` : '-')
            : (latestRecord.current_value || '-');
        const latestRisk = riskBase && typeof riskBase.current_value === 'number'
            ? getRiskLevel(riskBase.current_value, isBp ? 'BP_SYSTOLIC' : measureType).level
            : '-';


        setSelectedUser({
            userId: user.id,
            name: latestRecord.name || '-',
            birthdate: latestRecord.birthdate || '-',
            gender: latestRecord.gender || '-',
            relationship: latestRecord.relationship || '-', // 관계 추가
            management_group: latestRecord.group_name || '-',
            chartData: userData,
            safeRange: safeRangeValue,
            diastolicRange: diastolicRangeValue,
            latestValue,
            latestRisk,
            history: userData
        });
        setIsModalOpen(true);
        // console.log('safeRange:', safeRangeValue, diastolicRangeValue); // 모달 열릴 때
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedUser(null);
    };

    // 차트 x 축 시작점: 선택한 기록 중 가장 이른 날짜.
    const sortedHistory = selectedUser && selectedUser.history
        ? [...selectedUser.history].sort((a, b) => new Date(a.measure_date) - new Date(b.measure_date))
        : [];
    const minDate = sortedHistory.length > 0 ? sortedHistory[0].measure_date : undefined;

    const formatDay = (dateString) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return '—';
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    const formatValue = (type, value) => (
        type === 'BMI' && typeof value === 'number' ? value.toFixed(1) : value
    );

    const isBloodPressure = activeTab === 'BP_SYSTOLIC' || activeTab === 'BP_DIASTOLIC';

    return (
        <div className="guardian-page">
            <main className="sds-page">
                <div className="sds-page-header">
                    <div>
                        <h1 className="sds-page-header__title">보호자 대시보드</h1>
                        <p className="sds-page-header__sub">관리 중인 사용자 {mappedUsers.length}명</p>
                    </div>
                    <div className="sds-page-header__actions">
                        <button
                            className="sds-btn sds-btn--secondary"
                            onClick={() => {
                                localStorage.clear();
                                navigate('/');
                            }}
                        >
                            <LogOut size={16} aria-hidden="true" />
                            로그아웃
                        </button>
                    </div>
                </div>

                {healthReportModalUser && (
                    <HealthReportModal user={healthReportModalUser} onClose={() => setHealthReportModalUser(null)} />
                )}

                {mappedUsers.length === 0 ? (
                    <div className="sds-empty guardian-page__empty">관리 중인 사용자가 없습니다.</div>
                ) : (
                    <div className="guardian-page__people">
                        {mappedUsers.map((user) => {
                            // 피보호자 행에 저장된 management_id 를 쓴다(배열 순서 추측 없음).
                            const userManagementId = user.management_id ?? null;
                            const userHealthData = userManagementId ? data[userManagementId] || [] : [];

                            // 측정 타입별 가장 최근 기록
                            const getLatestData = (measureType) => {
                                const filteredData = userHealthData.filter(r => r.measure_type === measureType);
                                if (filteredData.length === 0) return null;
                                return [...filteredData].sort((a, b) => new Date(b.measure_date) - new Date(a.measure_date))[0];
                            };

                            const latestDate = userHealthData.length > 0
                                ? userHealthData.reduce((latest, record) => (
                                    new Date(record.measure_date) > new Date(latest) ? record.measure_date : latest
                                ), userHealthData[0].measure_date)
                                : null;

                            const cardUser = { ...user, managementId: userManagementId };
                            const wearsRing = user.app_user_id && smartRingAppUserIds.has(Number(user.app_user_id));

                            return (
                                <section key={user.id || user.user_id} className="guardian-page__person" aria-labelledby={`guardian-person-${user.id || user.user_id}`}>
                                    <div className="guardian-page__person-head">
                                        <div className="guardian-page__person-title">
                                            <h2 id={`guardian-person-${user.id || user.user_id}`} className="guardian-page__person-name">{user.name}</h2>
                                            <span className="guardian-page__person-meta">
                                                {latestDate ? `최근 측정 ${formatDay(latestDate)}` : '측정 기록 없음'}
                                            </span>
                                        </div>
                                        <div className="guardian-page__person-actions">
                                            {/* 스마트링 착용자에게만 보인다. VitalsDetail 은 app_user_id 로 연다. */}
                                            {wearsRing && (
                                                <button
                                                    className="sds-btn sds-btn--secondary sds-btn--sm"
                                                    onClick={() => navigate(`/VitalsDetail/${user.app_user_id}`, {
                                                        state: { patientName: user.name, from: 'guardian' }
                                                    })}
                                                >
                                                    <Watch size={16} aria-hidden="true" />
                                                    스마트링 바이탈
                                                </button>
                                            )}
                                            <button
                                                className="sds-btn sds-btn--weak sds-btn--sm"
                                                onClick={() => setHealthReportModalUser(cardUser)}
                                            >
                                                <FileText size={16} aria-hidden="true" />
                                                건강보고서
                                            </button>
                                        </div>
                                    </div>

                                    <div className="guardian-page__metrics">
                                        {METRICS.map(({ type, label, unit }) => {
                                            const latestData = getLatestData(type);
                                            if (!latestData) {
                                                return (
                                                    <div key={type} className="sds-metric-card guardian-page__metric guardian-page__metric--empty">
                                                        <div className="sds-metric-card__head">
                                                            {label}
                                                            <RiskBadge level="기록 없음" />
                                                        </div>
                                                        <div className="sds-metric-card__value">—</div>
                                                    </div>
                                                );
                                            }
                                            const risk = getRiskLevel(latestData.current_value, type);
                                            return (
                                                <button
                                                    key={type}
                                                    className="sds-metric-card guardian-page__metric"
                                                    onClick={() => handleUserClick(cardUser, type)}
                                                    aria-label={`${label} ${formatValue(type, latestData.current_value)}${unit ? ` ${unit}` : ''}, ${risk.level}, 기록 자세히 보기`}
                                                >
                                                    <span className="sds-metric-card__head">
                                                        {label}
                                                        <RiskBadge level={risk.level} />
                                                    </span>
                                                    <span className="sds-metric-card__value">
                                                        {formatValue(type, latestData.current_value)}
                                                        {unit && <span className="sds-metric-card__unit">{unit}</span>}
                                                    </span>
                                                    <span className="sds-metric-card__delta">{formatDay(latestData.measure_date)}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </section>
                            );
                        })}
                    </div>
                )}
            </main>

            {isModalOpen && selectedUser && (
                <div className="sds-modal-overlay" onClick={closeModal}>
                    <div
                        className="sds-modal sds-modal--wide guardian-page__modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="guardian-detail-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="sds-modal__head">
                            <h2 id="guardian-detail-title" className="sds-modal__title">{MODAL_TITLES[activeTab] || '측정 기록'}</h2>
                            <button className="sds-btn sds-btn--ghost sds-btn--sm sds-btn--icon" onClick={closeModal} aria-label="닫기">
                                <X size={16} aria-hidden="true" />
                            </button>
                        </div>

                        <div className="sds-modal__body">
                            <dl className="guardian-page__summary">
                                <div>
                                    <dt>사용자명</dt>
                                    <dd>{selectedUser.name}</dd>
                                </div>
                                <div>
                                    <dt>현재 위험도</dt>
                                    <dd>{selectedUser.latestRisk && selectedUser.latestRisk !== '-'
                                        ? <RiskBadge level={selectedUser.latestRisk} short={false} />
                                        : '—'}</dd>
                                </div>
                                <div>
                                    <dt>최종입력 수치</dt>
                                    <dd>{formatValue(activeTab, selectedUser.latestValue)}</dd>
                                </div>
                                <div>
                                    <dt>최종입력 일자</dt>
                                    <dd>
                                        {selectedUser.history && selectedUser.history.length > 0
                                            ? dateFormatter(selectedUser.history.reduce((latest, record) => (
                                                new Date(record.measure_date) > new Date(latest) ? record.measure_date : latest
                                            ), selectedUser.history[0].measure_date))
                                            : '기록 없음'}
                                    </dd>
                                </div>
                            </dl>

                            <h3 className="sds-section-title guardian-page__section-title">측정 기록</h3>
                            <div className="guardian-page__chart">
                                <Line
                                    key={selectedUser?.userId + '-' + activeTab}
                                    data={{
                                        datasets: isBloodPressure ? [
                                            {
                                                label: '수축기',
                                                data: sortedHistory
                                                    .filter(record => record.measure_type === 'BP_SYSTOLIC')
                                                    .map(record => ({
                                                        x: new Date(record.measure_date).toISOString().split('T')[0],
                                                        y: record.current_value
                                                    })),
                                                borderColor: chartColor['1'],
                                                backgroundColor: 'transparent',
                                                fill: false,
                                                tension: 0.4,
                                                borderWidth: 2,
                                                pointRadius: 0,
                                                pointHoverRadius: 0,
                                                measure_type: 'BP_SYSTOLIC'
                                            },
                                            {
                                                label: '이완기',
                                                data: sortedHistory
                                                    .filter(record => record.measure_type === 'BP_DIASTOLIC')
                                                    .map(record => ({
                                                        x: new Date(record.measure_date).toISOString().split('T')[0],
                                                        y: record.current_value
                                                    })),
                                                borderColor: chartColor['5'],
                                                backgroundColor: 'transparent',
                                                fill: false,
                                                tension: 0.4,
                                                borderWidth: 2,
                                                pointRadius: 0,
                                                pointHoverRadius: 0,
                                                measure_type: 'BP_DIASTOLIC'
                                            }
                                        ] : [
                                            {
                                                label: activeTab === 'BLOOD_GLUCOSE' ? '식전/공복혈당'
                                                    : activeTab === 'BLOOD_GLUCOSE_MEAL' ? '식후혈당'
                                                        : activeTab === 'BMI' ? '체질량지수'
                                                            : '측정값',
                                                data: sortedHistory.map(record => ({
                                                    x: new Date(record.measure_date).toISOString().split('T')[0],
                                                    y: activeTab === 'BMI' && typeof record.current_value === 'number' ? record.current_value.toFixed(1) : record.current_value
                                                })),
                                                borderColor: chartColor['1'],
                                                backgroundColor: 'transparent',
                                                fill: false,
                                                tension: 0.4,
                                                borderWidth: 2,
                                                pointRadius: 0,
                                                pointHoverRadius: 0,
                                                measure_type: activeTab
                                            }
                                        ]
                                    }}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        scales: {
                                            y: {
                                                beginAtZero: false,
                                                suggestedMin: activeTab === 'BLOOD_GLUCOSE' ? 40 :
                                                    activeTab === 'BMI' ? 15 :
                                                        activeTab === 'BP_SYSTOLIC' ? 60 : 40,
                                                suggestedMax: activeTab === 'BLOOD_GLUCOSE' ? 200 :
                                                    activeTab === 'BMI' ? 35 :
                                                        activeTab === 'BP_SYSTOLIC' ? 180 : 160,
                                                title: {
                                                    display: true,
                                                    text: activeTab === 'BLOOD_GLUCOSE' ? '식전/공복혈당 수치' :
                                                        activeTab === 'BMI' ? 'BMI 수치' :
                                                            '혈압 수치',
                                                    color: textColor['2'],
                                                    font: { size: 13, weight: '600' }
                                                },
                                                grid: { color: chartColor.grid },
                                                ticks: { color: textColor['2'], font: { size: 12 } }
                                            },
                                            x: {
                                                type: 'time',
                                                adapters: { date: { locale: ko } },
                                                time: { unit: 'day', displayFormats: { day: 'MM-dd' } },
                                                min: minDate,
                                                title: {
                                                    display: true,
                                                    text: '날짜',
                                                    color: textColor['2'],
                                                    font: { size: 13, weight: '600' }
                                                },
                                                grid: { color: chartColor.grid },
                                                ticks: {
                                                    color: textColor['2'],
                                                    font: { size: 12 },
                                                    source: 'auto',
                                                    maxRotation: 0,
                                                    callback: function (value) {
                                                        const labelDate = new Date(value);
                                                        if (isNaN(labelDate)) return '';
                                                        return `${String(labelDate.getMonth() + 1).padStart(2, '0')}-${String(labelDate.getDate()).padStart(2, '0')}`;
                                                    }
                                                }
                                            }
                                        },
                                        plugins: {
                                            legend: {
                                                display: true,
                                                position: 'top',
                                                labels: { color: textColor['2'], font: { size: 12 } }
                                            },
                                            tooltip: {
                                                mode: 'index',
                                                intersect: false,
                                                callbacks: {
                                                    label: function (context) {
                                                        let label = context.dataset.label || '';
                                                        if (label) {
                                                            label += ': ';
                                                        }
                                                        label += context.parsed.y;
                                                        return label;
                                                    }
                                                }
                                            },
                                            safeRangesPlugin: true
                                        },
                                        interaction: {
                                            mode: 'nearest',
                                            axis: 'x',
                                            intersect: false
                                        },
                                        safeRanges: isBloodPressure
                                            ? [
                                                ...((Array.isArray(systolicRange) && systolicRange.length > 0)
                                                    ? systolicRange
                                                    : [
                                                        { min: 90, max: 120, color: chartColor['band-normal'], lineColor: statusColor.normal.line },
                                                        { min: 120, max: 140, color: chartColor['band-caution'], lineColor: statusColor.caution.line }
                                                    ]),
                                                ...((Array.isArray(diastolicRange) && diastolicRange.length > 0)
                                                    ? diastolicRange
                                                    : [
                                                        { min: 60, max: 80, color: chartColor['band-normal'], lineColor: statusColor.normal.line },
                                                        { min: 80, max: 90, color: chartColor['band-caution'], lineColor: statusColor.caution.line }
                                                    ])
                                            ]
                                            : activeTab === 'BLOOD_GLUCOSE' ? [
                                                { min: 70, max: 100, color: chartColor['band-normal'], lineColor: statusColor.normal.line },
                                                { min: 100, max: 125, color: chartColor['band-caution'], lineColor: statusColor.caution.line }
                                            ] : activeTab === 'BLOOD_GLUCOSE_MEAL' ? [
                                                { min: 90, max: 140, color: chartColor['band-normal'], lineColor: statusColor.normal.line },
                                                { min: 140, max: 200, color: chartColor['band-caution'], lineColor: statusColor.caution.line }
                                            ] : activeTab === 'BMI' ? [
                                                { min: 18.5, max: 23, color: chartColor['band-normal'], lineColor: statusColor.normal.line },
                                                { min: 23, max: 25, color: chartColor['band-caution'], lineColor: statusColor.caution.line }
                                            ] : []
                                    }}
                                />
                            </div>

                            <div className="sds-table-wrap guardian-page__history">
                                <table className="sds-table">
                                    <thead>
                                        <tr>
                                            <th>날짜</th>
                                            <th>{isBloodPressure ? '수축기/이완기 혈압' : '측정값'}</th>
                                            <th>위험도</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[...new Set(selectedUser.history.map(record => record.measure_date))]
                                            .map((date, index) => {
                                                const recordsForDate = selectedUser.history.filter(r => r.measure_date === date);
                                                const systolicRecord = recordsForDate.find(r => r.measure_type === 'BP_SYSTOLIC');
                                                const diastolicRecord = recordsForDate.find(r => r.measure_type === 'BP_DIASTOLIC');
                                                const otherRecord = recordsForDate.find(r => !['BP_SYSTOLIC', 'BP_DIASTOLIC'].includes(r.measure_type));

                                                const value = activeTab === 'BP_SYSTOLIC'
                                                    ? `${systolicRecord?.current_value || '-'}/${diastolicRecord?.current_value || '-'}`
                                                    : formatValue(activeTab, otherRecord?.current_value);

                                                const riskValue = activeTab === 'BP_SYSTOLIC'
                                                    ? systolicRecord?.current_value
                                                    : otherRecord?.current_value;

                                                return (
                                                    <tr key={index}>
                                                        <td className="sds-table__muted">{dateFormatter(date)}</td>
                                                        <td>{value ?? '—'}</td>
                                                        <td>{riskValue ? <RiskBadge level={getRiskLevel(riskValue, activeTab).level} short={false} /> : '—'}</td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// HealthReportModal: 모달창에 HealthReport 실제 데이터 렌더링
const HealthReportModal = ({ user, onClose }) => {
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchHealthReport = async () => {
            try {
                setLoading(true);
                setError(null);

                // 토큰에서 management_id 찾기
                const token = localStorage.getItem('token');
                let managementId = null;

                // 전달받은 managementId가 있으면 사용, 없으면 토큰에서 찾기
                if (user.managementId) {
                    managementId = user.managementId;
                } else if (token) {
                    try {
                        const decodedToken = jwtDecode(token);

                        if (decodedToken.packages) {
                            // 사용자 ID로 해당하는 management_id 찾기 (dependent_id와 매칭)
                            const userPackage = decodedToken.packages.find(pkg =>
                                pkg.dependent_id === user.id ||
                                pkg.dependent_id === user.user_id ||
                                pkg.dependent_id === parseInt(user.id) ||
                                pkg.dependent_id === parseInt(user.user_id)
                            );

                            if (userPackage) {
                                managementId = userPackage.management_id;
                            }
                        }
                    } catch (error) {
                        console.error('토큰 디코딩 실패:', error);
                    }
                }

                if (!managementId) {
                    setError('사용자의 관리 ID를 찾을 수 없습니다.');
                    setLoading(false);
                    return;
                }

                const requestUrl = `${API_BASE_URL}/api/guardian-health-report/${managementId}`;

                const response = await axios.get(requestUrl, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.data.success) {
                    setReportData(response.data.data);
                } else {
                    setError('건강보고서를 불러오는 데 실패했습니다.');
                }
            } catch (error) {
                console.error('건강보고서 조회 오류:', error);
                setError('건강보고서를 불러오는 중 오류가 발생했습니다.');
            } finally {
                setLoading(false);
            }
        };

        fetchHealthReport();
    }, [user]);

    if (loading || error) {
        return (
            <div className="sds-modal-overlay" onClick={onClose}>
                <div className="sds-modal" role="dialog" aria-modal="true" aria-labelledby="guardian-report-title" onClick={(e) => e.stopPropagation()}>
                    <div className="sds-modal__head">
                        <h2 id="guardian-report-title" className="sds-modal__title">건강보고서</h2>
                        <button className="sds-btn sds-btn--ghost sds-btn--sm sds-btn--icon" onClick={onClose} aria-label="닫기">
                            <X size={16} aria-hidden="true" />
                        </button>
                    </div>
                    <div className="sds-modal__body">
                        {loading
                            ? <div className="sds-notice" role="status">건강보고서를 불러오는 중...</div>
                            : <div className="sds-notice sds-notice--error" role="alert">{error}</div>}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="sds-modal-overlay" onClick={onClose}>
            <div className="sds-modal sds-modal--full" role="dialog" aria-modal="true" aria-label="건강보고서" onClick={(e) => e.stopPropagation()}>
                <div className="sds-modal__body guardian-page__report">
                    <HealthReport testData={reportData} onClose={onClose} />
                </div>
            </div>
        </div>
    );
};
